import type {
  IUserDoc,
  IUserDocAuditEntry,
  IUserDocContent,
  IUserSurvey,
  IUserSurveyAnswersBody,
  IUserSurveyResult,
} from '@logz-pkg/enums';
import { httpService } from '../../core/http/request.provider';

const BASE = '/orioniq-be/user-docs';

// Per-user memory (USER.md). Mirrors accountDocsApiService but hits the user-scoped BFF path — orioniq-be
// resolves {accountId, userId} from the session, so no ids are passed here.
class UserDocsApiService {
  private urls = {
    docs: BASE,
    doc: (name: string): string => `${BASE}/${encodeURIComponent(name)}`,
    audit: (name: string): string => `${BASE}/${encodeURIComponent(name)}/audit`,
    survey: `${BASE}/survey`,
  };

  list = async (): Promise<IUserDoc[]> => {
    const { data } = await httpService.get<IUserDoc[]>(this.urls.docs);

    return data;
  };

  getContent = async (name: string): Promise<IUserDocContent> => {
    const { data } = await httpService.get<IUserDocContent>(this.urls.doc(name));

    return data;
  };

  upsert = async ({ name, content }: { name: string; content: string }): Promise<unknown> => {
    const { data } = await httpService.put(this.urls.doc(name), { content });

    return data;
  };

  delete = async (name: string): Promise<unknown> => {
    const { data } = await httpService.delete(this.urls.doc(name));

    return data;
  };

  getAudit = async (name: string): Promise<IUserDocAuditEntry[]> => {
    const { data } = await httpService.get<IUserDocAuditEntry[]>(this.urls.audit(name));

    return data;
  };

  /** The onboarding survey, and whether this user already has a USER.md (`answered`). */
  getSurvey = async (): Promise<IUserSurvey> => {
    const { data } = await httpService.get<IUserSurvey>(this.urls.survey);

    return data;
  };

  /** Answers the survey; orioniq-be composes and writes USER.md. Rejects with a 409 when one already exists. */
  answerSurvey = async (body: IUserSurveyAnswersBody): Promise<IUserSurveyResult> => {
    const { data } = await httpService.post<IUserSurveyResult>(this.urls.survey, body);

    return data;
  };
}

export const userDocsApiService = new UserDocsApiService();
